import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import { filtersLibrary } from "@/components/filter/filter.types.ts";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useWorkflowActions } from "@/pages/workflows/workflow-actions-hook.ts";
import { WorkflowTriggerCaption } from "@/pages/workflows/components/WorkflowTriggerCaption.tsx";
import { useWorkflows } from "@/pages/workflows/workflow-hook.tsx";
import { useWorkflowFolders } from "@/queries/workflows/workflow-folder.query.ts";
import { useWorkflowsSearch } from "@/queries/workflows/workflow.query.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { ApiWorkflowFilter, ApiWorkflowListItem } from "@/types/workflows/workflow.types.ts";
import {
  Avatar,
  ButtonLink,
  ConfirmationModal,
  DropdownMenu,
  DropdownMenuItem,
  EmptyStateBase,
  FontAwesomeIcon,
  ListItem,
  Page,
  PaginationBlock,
  Stack,
  Table,
  Text,
  useModals,
} from "@skailer-com/uikit";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

export const WorkflowLibraryListView = () => {
  const library = filtersLibrary();
  const { renderDate } = useFormatter();
  const { id } = useParams<{ id: string }>();
  const [, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { canIEdit } = usePermissions();
  const { openModal } = useModals();
  const { t } = useTranslation();
  const canManageWorkflows = canIEdit(ApiPermission.workflows_manage_all);
  const canCreateWorkflows = canIEdit(ApiPermission.workflows_create);
  const {
    deleteWorkflow,
    isWorkflowDeletePending,
    editWorkflow,
    toggleWorkflowIsActive,
    isWorkflowActivationPending,
    duplicateWorkflow,
    isWorkflowDuplicatePending,
  } = useWorkflowActions();

  const {
    filter: { filter },
  } = useAppFilter<ApiWorkflowFilter>({
    filters: [library.page_number],
    source: "Workflows",
  });

  const { data: workflowFolders, isLoading: isWorkflowFoldersLoading } = useWorkflowFolders();
  const isAllView = id === "all";
  const selectedFolder = useMemo(() => {
    if (isAllView) return null;
    const folderId = Number(id);
    if (Number.isNaN(folderId)) return undefined;
    return workflowFolders?.find((folder) => folder.id === folderId);
  }, [id, isAllView, workflowFolders]);

  const workflowsFilter = useMemo<ApiWorkflowFilter>(() => {
    if (isAllView) return filter;
    if (selectedFolder) return { ...filter, folder_id: selectedFolder.id };
    return filter;
  }, [filter, isAllView, selectedFolder]);

  const {
    data: workflows,
    isLoading,
    isFetching,
  } = useWorkflowsSearch({
    filter: workflowsFilter,
    enabled: isAllView || selectedFolder != undefined,
  });

  const { getWorkflowTriggerTypeIcon, getWorkflowTriggerDescription, getWorkflowStatusTag } =
    useWorkflows();

  const columns = useMemo<ColumnDef<ApiWorkflowListItem>[]>(() => {
    const inner: ColumnDef<ApiWorkflowListItem>[] = [
      {
        header: t("workflow:workflow"),
        cell: (row) => (
          <ListItem
            to={`/workflows/${row.row.original.id}/summary`}
            icon={
              row.row.original.is_shared && row.row.original.emoji ? (
                <Avatar emoji={row.row.original.emoji} />
              ) : (
                <Avatar icon={getWorkflowTriggerTypeIcon(row.row.original.trigger_type)} />
              )
            }
            title={row.row.original.title}
            captionSlot={
              <WorkflowTriggerCaption
                workflow={row.row.original}
                triggerDescription={getWorkflowTriggerDescription(row.row.original)}
              />
            }
            forceShowAvatar={true}
            truncate={true}
            behavior={"card"}
          />
        ),
        accessorKey: "title",
        ...ColumnSizes.employee,
        meta: { disableRowClick: true },
      },
      {
        header: t("common:status"),
        cell: (row) => <Stack items={"start"}>{getWorkflowStatusTag(row.row.original)}</Stack>,
        accessorKey: "status",
        ...ColumnSizes.status,
      },
      {
        header: t("workflow:runs_count"),
        cell: (row) => (
          <Text>{t("plurals:times_count", { count: row.row.original.runs_count })}</Text>
        ),
        accessorKey: "runs_count",
        ...ColumnSizes.status,
      },
      {
        header: t("workflow:last_run_at"),
        cell: (row) => (
          <Text>
            {row.row.original.datetime_last_run_at
              ? renderDate(row.row.original.datetime_last_run_at)
              : "-"}
          </Text>
        ),
        accessorKey: "last_run_at",
        ...ColumnSizes.status,
      },
    ];

    return inner;
  }, [
    getWorkflowStatusTag,
    getWorkflowTriggerDescription,
    getWorkflowTriggerTypeIcon,
    renderDate,
    t,
  ]);

  const renderRowAction = useCallback(
    (row: Row<ApiWorkflowListItem>) => {
      if (!canManageWorkflows) return null;
      const workflow = row.original;
      return (
        <DropdownMenu>
          <DropdownMenuItem
            onClick={() => editWorkflow(workflow.id)}
            leftIcon={<FontAwesomeIcon icon={"fa-light fa-pen"} />}
            title={t("common:edit")}
          />
          <DropdownMenuItem
            onClick={() => {
              openModal(ConfirmationModal, {
                question: workflow.is_active
                  ? t("workflow:pause_automation")
                  : t("workflow:activate_automation"),
                text: workflow.is_active
                  ? t("workflow:pause_automation_confirm")
                  : t("workflow:activate_automation_confirm"),
                acceptTitle: t("common:confirm"),
                onAccept: async () => {
                  await toggleWorkflowIsActive({
                    workflowId: workflow.id,
                    currentWorkflowVersionId: workflow.current_version_id,
                    is_active: workflow.is_active,
                  });
                },
              });
            }}
            leftIcon={
              <FontAwesomeIcon
                icon={workflow.is_active ? "fa-light fa-circle-pause" : "fa-light fa-circle-play"}
              />
            }
            title={
              workflow.is_active
                ? t("workflow:pause_automation")
                : t("workflow:activate_automation")
            }
          />
          {canCreateWorkflows && (
            <DropdownMenuItem
              onClick={() => duplicateWorkflow(workflow.id)}
              leftIcon={<FontAwesomeIcon icon={"fa-light fa-copy"} />}
              title={t("common:duplicate")}
            />
          )}
          <DropdownMenuItem
            onClick={() => {
              openModal(ConfirmationModal, {
                question: t("common:attention"),
                text: t("common:delete_confirm"),
                acceptTitle: t("common:delete"),
                onAccept: async () => {
                  await deleteWorkflow(workflow.id);
                },
              });
            }}
            leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
            variant={"menuDanger"}
            title={t("common:delete") + "..."}
          />
        </DropdownMenu>
      );
    },
    [
      canManageWorkflows,
      canCreateWorkflows,
      deleteWorkflow,
      duplicateWorkflow,
      editWorkflow,
      openModal,
      t,
      toggleWorkflowIsActive,
    ]
  );

  const viewTitle = useMemo(() => {
    if (isAllView) return t("workflow:all_workflows");
    if (isWorkflowFoldersLoading) return "";
    return selectedFolder?.title ?? t("common:nothing_found");
  }, [isAllView, isWorkflowFoldersLoading, selectedFolder, t]);

  const showEmptyState = !isFetching && workflows != undefined && workflows.items.length === 0;

  return (
    <Page className="h-full">
      <Page.Header title={viewTitle} showBack={true} navigateTo={"/workflows/library"}>
        {canIEdit(ApiPermission.workflows_create) && (
          <ButtonLink
            href={
              selectedFolder ? `/workflows/new?folder_id=${selectedFolder.id}` : "/workflows/new"
            }
          >
            {t("workflow:create_new")}
          </ButtonLink>
        )}
      </Page.Header>

      <Page.Content stretch>
        {selectedFolder === undefined && !isAllView && !isWorkflowFoldersLoading ? (
          <EmptyStateNothingFound />
        ) : (
          <Page.StickyLayout
            footer={
              <PaginationBlock
                metadata={workflows?.metadata}
                onPageChange={(page) => {
                  setSearchParams((searchParams) => {
                    searchParams.set("page", page.toString());
                    return searchParams;
                  });
                }}
              />
            }
          >
            <Table
              stickyHeader={true}
              onRowClick={(row) => navigate(`/workflows/${row.original.id}/summary`)}
              renderRowAction={renderRowAction}
              data={workflows?.items || []}
              columns={columns}
              showSkeleton={isLoading || (isWorkflowFoldersLoading && !isAllView)}
              isFetching={
                isLoading ||
                isFetching ||
                isWorkflowDeletePending ||
                isWorkflowActivationPending ||
                isWorkflowDuplicatePending
              }
              showNotFoundError={showEmptyState}
              notFoundComponent={
                <EmptyStateBase
                  emoji={":sparkles:"}
                  title={t("workflow:empty_workflows_title")}
                  description={t("workflow:empty_workflows_description")}
                  variant="primary"
                  primaryButton={
                    canIEdit(ApiPermission.workflows_create) ? (
                      <ButtonLink
                        href={
                          selectedFolder
                            ? `/workflows/new?folder_id=${selectedFolder.id}`
                            : "/workflows/new"
                        }
                      >
                        {t("workflow:create_new")}
                      </ButtonLink>
                    ) : undefined
                  }
                />
              }
            />
          </Page.StickyLayout>
        )}
      </Page.Content>
    </Page>
  );
};
